import React, { useCallback, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Pager, Paging, Scrolling, SearchPanel, Summary, TotalItem } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { Button as DevXButton } from 'devextreme-react/button';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from 'Common/Loader';
import StoreSelectOptions from '../../InwardReport/components/StoreSelectOptions';
import type { ValueChangedEvent } from 'devextreme/ui/date_range_box';
import DateRangeBox from 'devextreme-react/cjs/date-range-box';
import { GetAdvancePaymentReport } from '../services/advanceReport.service';


interface AdvancePaymentData {
  uid: string;
  storeCode: string;
  storeName: string;
  customerName: string;
  advanceNo: string;
  advanceDate: string;
  mobile: string;
  paymentType: string;
  amount: number;
  customerCreditAmount: number;
}


const AdvanceReportDataList: React.FC = () => {
  const [selectedStore, setSelectedStore] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const msInDay = 1000 * 60 * 60 * 24;
  const now = new Date();
  const initialValue: [Date, Date] = [
    new Date(now.getTime() - msInDay * 3),
    new Date(now.getTime() + msInDay * 3),
  ];
  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(initialValue);
  const onCurrentValueChange = useCallback((e: ValueChangedEvent) => {
    const value = e.value as [Date, Date] | undefined;
    if (value && value[0] && value[1]) {
      const [startDate, endDate] = value;
      setSelectedRange([startDate, endDate]);
    } else {
      setSelectedRange(null);
    }
  }, []);
  function formatDateTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const [dataGridInstance, setDataGridInstance] = useState<any>(null);
  const gridRef = useRef(null);

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "uid",
      load: async () => {
        try {
          setLoading(true);

          const parameterList = {
            StoreId: selectedStore || "0",
            fromDate: selectedRange ? formatDateTime(selectedRange[0]) : "",
            toDate: selectedRange ? formatDateTime(selectedRange[1]) : ""
          };

          const response = await GetAdvancePaymentReport(parameterList);
          // console.log(response)
          const dataWithId = (response.data ?? []).map((item: any, index: number) => ({
            ...item,
            uid: `${Date.now()}_${index}`
          }));

          return {
            data: dataWithId,
            totalCount: dataWithId.length
          };
        } catch (error) {
          console.error("Data loading error:", error);
          return { data: [], totalCount: 0 };
        } finally {
          setLoading(false);
        }
      }
    });
  }, [selectedStore, selectedRange]);


  const renderSerialNumber = useCallback((cellData: any) => {
    const rowIndex = cellData.rowIndex + 1 + cellData.component.pageIndex() * cellData.component.pageSize();
    return <span>{rowIndex}</span>;
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!dataGridInstance) return;

    setLoading(true);
    try {
      const dataSource = dataGridInstance.getDataSource();
      dataSource.load().then((data: AdvancePaymentData[]) => {
        //  Columns for Advance Payment
        const columns = [
          { key: 'storeName', name: 'Store Name' },
          { key: 'storeCode', name: 'Store Code' },
          { key: 'customerName', name: 'Customer Name' },
          { key: 'advanceNo', name: 'Advance No' },
          { key: 'advanceDate', name: 'Advance Date' },
          { key: 'mobile', name: 'Mobile' },
          { key: 'paymentType', name: 'Payment Type' },
          { key: 'amount', name: 'Amount' },
          { key: 'customerCreditAmount', name: 'Customer Credit Amount' },
        ];

        let excelData: any[] = [];

        if (data.length > 0) {
          // Header row
          excelData.push(columns.reduce((obj, col) => ({
            ...obj,
            [col.name]: col.name
          }), {}));

          // Data rows
          data.forEach(item => {
            excelData.push({
              'Store Name': item.storeName || '',
              'Store Code': item.storeCode || '',
              'Customer Name': item.customerName || '',
              'Advance No': item.advanceNo || '',
              'Advance Date': item.advanceDate ? item.advanceDate.split("T")[0] : '',
              'Mobile': item.mobile || '',
              'Payment Type': item.paymentType || '',
              'Amount': item.amount || 0,
              'Customer Credit Amount': item.customerCreditAmount || 0
            });
          });
        } else {
          // Only header row if no data
          excelData.push(columns.reduce((obj, col) => ({
            ...obj,
            [col.name]: col.name
          }), {}));
        }

        const worksheet = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });
        const wscols = columns.map(col => ({ width: col.name.length + 5 }));
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Advance Report");

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });

        // ✅ Updated filename
        saveAs(blob, `Advance_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setLoading(false);
    }
  }, [dataGridInstance]);


  const handleStoreChangeFromChild = (value: string) => {
    setSelectedStore(value || undefined);
  };
  const isMainStore = localStorage.getItem('isMainStore')
  return (
    <React.Fragment>
      {loading && <Loader />}

      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className='d-flex justify-content-between align-items-center'>
              <h5 className='m-0'>Advance Report</h5>
              <DevXButton
                icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                hint="Export to Excel"
                onClick={handleExportExcel}
              />
            </Card.Header>
            <div className='d-flex'>
              <div style={{ marginLeft: '20px', paddingTop: '10px' }}>
                {isMainStore === 'true' && (
                  <StoreSelectOptions onStoreChange={handleStoreChangeFromChild} />
                )}
              </div>
              <div style={{ marginLeft: '20px', paddingTop: '8px' }}>
                <DateRangeBox
                  width={300}
                  ref={gridRef}
                  defaultValue={['', '']}
                  displayFormat="dd/MM/yyyy"
                  showClearButton={true}
                  acceptCustomValue={false}
                  onValueChanged={onCurrentValueChange}
                />
              </div>
            </div>
            <Card.Body>
              <DataGrid
                dataSource={dataSource}
                showRowLines={true}
                keyExpr="id"
                showBorders={true}
                columnAutoWidth={true}
                onInitialized={(e) => setDataGridInstance(e.component)}
                onContentReady={(e) => {
                  const hasData = e.component.totalCount() > 0;
                  e.component.option("pager.visible", hasData);
                  e.component.option("paging.visible", hasData);
                }}
              >
                <Pager
                  showPageSizeSelector={true}
                  allowedPageSizes={[10, 25, 50, 100]}
                  showNavigationButtons={true}
                  displayMode="full"
                  showInfo={true}
                />
                <Summary>
                  <TotalItem />
                </Summary>
                <Scrolling
                  mode='standard'
                  useNative={true}
                  scrollByContent={true}
                  scrollByThumb={true}
                />
                <Paging defaultPageSize={10} />
                {/* <SearchPanel visible width={240} placeholder="Search..." /> */}

                <Column caption="Sr.No." cellRender={renderSerialNumber} />
                <Column dataField="storeName" caption="Store Name" />
                <Column dataField="storeCode" caption="Store Code" />
                <Column dataField="customerName" caption="Customer Name" />
                <Column dataField="advanceNo" caption="Advance No" />
                <Column dataField="advanceDate" caption="Advance Date"
                  cellRender={(cellData) => cellData.value?.split("T")[0]} />
                <Column dataField="mobile" caption="Mobile" />
                <Column dataField="paymentType" caption="Payment Type" />
                <Column dataField="amount" caption="Amount" dataType="number"
                  format="#,##0.00" />
                <Column dataField="customerCreditAmount" caption="Customer Credit Amount" dataType="number"
                  format="#,##0.00" />

              </DataGrid>
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AdvanceReportDataList;
